// Admin user-management client (MP-OS-93). Admin-only endpoints — the server enforces
// the role; these just carry the session cookie like the rest of the API.

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export interface ManagedUser {
  username: string
  name: string
  email: string | null
  role: 'admin' | 'supplier'
  manufacturer: string | null
  // The scope's owner name from the server (MPD-98), for a stored value the picker no longer offers.
  manufacturer_label?: string | null
}

export interface UserUpsert {
  username: string
  password?: string // omit to keep the current one (rename / re-scope)
  name?: string
  email?: string
  role: 'admin' | 'supplier'
  manufacturer?: string | null
  // Email the login details with this password. Unset = the server's old rule (a new seat is
  // told, an existing one isn't), which is how a reset used to go out silently to nobody.
  notify?: boolean
}

// What the save actually did about telling the user. `requested` false means no mail was
// attempted; `sent` false with `requested` true means the provider refused or is unconfigured.
export interface SaveResult extends ManagedUser {
  invite_requested?: boolean
  invite_sent?: boolean
}

export async function fetchUsers(): Promise<ManagedUser[]> {
  const r = await fetch(`${API_BASE}/api/users`, { credentials: 'include' })
  if (!r.ok) throw new Error('Could not load the users.')
  return (await r.json()).users as ManagedUser[]
}

export async function saveUser(body: UserUpsert): Promise<SaveResult> {
  const r = await fetch(`${API_BASE}/api/users`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!r.ok) {
    const detail = await r.json().then((d) => d.detail).catch(() => null)
    throw new Error(detail || 'Could not save the user.')
  }
  return (await r.json()) as SaveResult
}

// Production's edge policy (Cloud Armor) refuses DELETE/PUT/PATCH, so deletions are POSTs.
export async function deleteUser(username: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/users/${encodeURIComponent(username)}/delete`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!r.ok) {
    const detail = await r.json().then((d) => d.detail).catch(() => null)
    throw new Error(detail || 'Could not delete the user.')
  }
}
